import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../contexts/ThemeContext';
import { getThemeColors } from '../utils/theme';
import { API_ENDPOINTS } from '../config.js';
import { validatePassword } from '../utils/passwordValidation';

export default function ResetPassword() {
  const { t, i18n } = useTranslation();
  const { isDark } = useTheme();
  const colors = getThemeColors(isDark);
  const { token } = useParams();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [tokenValid, setTokenValid] = useState(null); // null = checking, true/false = result

  // Validate token on mount
  useEffect(() => {
    if (!token) {
      setError(t('resetPassword.invalidToken') || 'Invalid or missing reset token.');
      setTokenValid(false);
      return;
    }
    
    // Basic token format validation (JWT has 3 parts separated by dots)
    const tokenParts = token.split('.');
    if (tokenParts.length !== 3) {
      setError(t('resetPassword.invalidToken') || 'Invalid reset token format.');
      setTokenValid(false);
      return;
    }
    
    setTokenValid(true);
  }, [token, t]);

  const handleSubmit = async e => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!token) {
      setError(t('resetPassword.invalidToken') || 'Invalid or missing reset token.');
      return;
    }

    if (!password || !confirmPassword) {
      setError(t('auth.enterEmailPassword') || 'Please enter password and confirmation.');
      return;
    }

    if (password !== confirmPassword) {
      setError(t('profile.passwordMismatch') || 'Passwords do not match.');
      return;
    }

    // Validate password strength
    const passwordValidation = validatePassword(password);
    if (!passwordValidation.isValid) {
      setError(passwordValidation.errors.join(' '));
      return;
    }

    setLoading(true);

    try {
      // Decode token properly - React Router may or may not decode it
      let decodedToken = token;
      try {
        // Try decoding - if it's already decoded, this will fail gracefully
        decodedToken = decodeURIComponent(token);
      } catch (e) {
        // Token might already be decoded, use as-is
        decodedToken = token;
      }

      const res = await fetch(API_ENDPOINTS.RESET_PASSWORD, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept-Language': i18n.language || 'en',
        },
        credentials: 'include',
        body: JSON.stringify({
          token: decodedToken,
          password: password,
        }),
      });

      const data = await res.json();

      if (!res.ok) {
        // Handle different error types
        const errorMessage = data.error || t('resetPassword.resetError') || 'Failed to reset password.';
        setError(errorMessage);
        setSuccess('');
        
        // If token is invalid or expired, redirect to forgot password after a delay
        if (res.status === 400 && (errorMessage.includes('token') || errorMessage.includes('invalid') || errorMessage.includes('expired'))) {
          setTimeout(() => {
            navigate('/forgot-password', { 
              state: { message: t('resetPassword.tokenExpired') || 'Reset token is invalid or expired. Please request a new one.' }
            });
          }, 3000);
        }
      } else {
        setSuccess(data.message || t('resetPassword.resetSuccess') || 'Password reset successfully!');
        setError('');
        setTimeout(() => navigate('/login', { 
          state: { message: t('resetPassword.loginWithNewPassword') || 'Password reset successfully! Please login with your new password.' }
        }), 2000);
      }
    } catch (err) {
      console.error('Reset password error:', err);
      setError(t('auth.serverError') || 'Server error. Please try again.');
      setSuccess('');
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = {
    width: '100%',
    padding: '12px 10px',
    margin: '6px 0 18px 0',
    borderRadius: 6,
    border: `1px solid ${colors.border}`,
    fontSize: 16,
    background: colors.backgroundHover,
    color: colors.text,
    outline: 'none',
    transition: 'border-color 0.2s, background-color 0.2s, color 0.2s',
  };

  // Show loading state while validating token
  if (tokenValid === null) {
    return (
      <div style={{minHeight: '100vh', background: colors.backgroundLight, display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'background-color 0.3s ease'}}>
        <div style={{background: colors.card, borderRadius: 16, boxShadow: `0 4px 24px ${colors.shadowHover}`, padding: 36, width: '100%', maxWidth: 400, textAlign: 'center'}}>
          <div style={{color: colors.text}}>{t('common.loading') || 'Loading...'}</div>
        </div>
      </div>
    );
  }

  // If token is invalid, show error and link to request new one
  if (tokenValid === false) {
    return (
      <div style={{minHeight: '100vh', background: colors.backgroundLight, display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'background-color 0.3s ease'}}>
        <div style={{background: colors.card, borderRadius: 16, boxShadow: `0 4px 24px ${colors.shadowHover}`, padding: 36, width: '100%', maxWidth: 400, transition: 'background-color 0.3s ease, box-shadow 0.3s ease'}}>
          <h2 style={{fontSize: 24, fontWeight: 800, color: colors.primary, marginBottom: 24}}>{t('resetPassword.title') || 'Reset Password'}</h2>
          {error && <div style={{color: colors.error, marginBottom: 12}}>{error}</div>}
          <div style={{marginTop: 18, textAlign: 'center'}}>
            <button 
              onClick={() => navigate('/forgot-password')}
              style={{
                width: '100%', 
                background: colors.primary, 
                color: 'white', 
                fontWeight: 700, 
                fontSize: 18, 
                borderRadius: 8, 
                padding: '14px 0', 
                border: 'none', 
                boxShadow: `0 2px 8px ${colors.shadow}`, 
                cursor: 'pointer',
                transition: 'background-color 0.2s',
              }}
              onMouseEnter={(e) => e.target.style.background = colors.primaryHover}
              onMouseLeave={(e) => e.target.style.background = colors.primary}
            >
              {t('resetPassword.requestNewToken') || 'Request New Reset Link'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{minHeight: '100vh', background: colors.backgroundLight, display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'background-color 0.3s ease'}}>
      <form onSubmit={handleSubmit} style={{background: colors.card, borderRadius: 16, boxShadow: `0 4px 24px ${colors.shadowHover}`, padding: 36, width: '100%', maxWidth: 400, transition: 'background-color 0.3s ease, box-shadow 0.3s ease'}}>
        <h2 style={{fontSize: 24, fontWeight: 800, color: colors.primary, marginBottom: 24}}>{t('resetPassword.title') || 'Reset Password'}</h2>
        {error && <div style={{color: colors.error, marginBottom: 12}}>{error}</div>}
        {success && <div style={{color: colors.success, marginBottom: 12}}>{success}</div>}
        <label style={{fontWeight: 600, color: colors.text}}>{t('auth.newPassword')}</label>
        <input 
          type="password" 
          value={password} 
          onChange={e => { setPassword(e.target.value); setError(''); setSuccess(''); }} 
          style={inputStyle} 
          autoComplete="new-password" 
          required 
        />
        <label style={{fontWeight: 600, color: colors.text}}>{t('auth.confirmPassword')}</label>
        <input 
          type="password" 
          value={confirmPassword} 
          onChange={e => { setConfirmPassword(e.target.value); setError(''); setSuccess(''); }} 
          style={inputStyle} 
          autoComplete="new-password" 
          required 
        />
        <button 
          type="submit" 
          disabled={loading} 
          style={{
            marginTop: 18, 
            width: '100%', 
            background: loading ? colors.textLight : colors.primary, 
            color: 'white', 
            fontWeight: 700, 
            fontSize: 18, 
            borderRadius: 8, 
            padding: '14px 0', 
            border: 'none', 
            boxShadow: `0 2px 8px ${colors.shadow}`, 
            cursor: loading ? 'not-allowed' : 'pointer', 
            opacity: loading ? 0.7 : 1,
            transition: 'background-color 0.2s',
          }}
          onMouseEnter={(e) => !loading && (e.target.style.background = colors.primaryHover)}
          onMouseLeave={(e) => !loading && (e.target.style.background = colors.primary)}
        >
          {loading ? (t('common.loading') || 'Loading...') : (t('resetPassword.resetButton') || 'Reset Password')}
        </button>
      </form>
    </div>
  );
}

